/**
 * «Δημοσίευση έκδοσης» — ό,τι έκανε το `node scripts/publish_agent_release.js --yes` μέσω ssh, από την οθόνη
 * «Εκδόσεις agent» (24/10/2026, μόνο super admin).
 *
 * Πρώτα ΔΟΚΙΜΗ: ο server λέει τι θα δημοσίευε από τον κώδικά του (έκδοση, commit, βιβλιοθήκες) και αν η έκδοση
 * υπάρχει ήδη. Η δημοσίευση στέλνει ΑΚΡΙΒΩΣ το commit που είδες — αν στο μεταξύ έγινε deploy, δεν δημοσιεύεται
 * κρυφά κάτι άλλο. Καμία έκδοση δεν φτάνει σε ταμείο χωρίς στόχο, οπότε εδώ δεν αλλάζει τίποτα στα καταστήματα.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Upload } from 'lucide-react';
import { organizationAPI } from '../services/api';
import { useToast } from './shared/Toast';
import { GHOST_BTN, INPUT_CLS, Notice, PRIMARY_BTN } from './shared/listPage';
import { rich } from './home/ui';
import { TFn } from './org/orgFormat';

export interface ReleaseDraft {
  version: string;
  commit: string;
  subject: string;
  committedAt: string;
  fileCount: number;
  sizeBytes: number;
  depsHash: string;
  published: null | { version: string; gitCommit: string | null; createdAt: string; sameContent: boolean | null };
  newest: null | { version: string; createdAt: string };
  depsChanged: boolean;
}

const NOTES_MAX = 1000;

const day = (iso: string) => new Date(iso).toLocaleDateString('el-GR', { timeZone: 'Europe/Athens', day: 'numeric', month: 'short', year: 'numeric' });

const AgentReleasePublish: React.FC<{ onClose: () => void; onPublished: (version: string) => void }> = ({ onClose, onPublished }) => {
  const { t: tRaw } = useTranslation(['orgDashboard', 'common']);
  const t = tRaw as unknown as TFn;
  const { showToast } = useToast();
  const uid = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<ReleaseDraft | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setDraft(await organizationAPI.getAgentReleaseDraft());
    } catch (e: any) {
      setLoadError(e?.response?.data?.error || t('orgDashboard:agent.publish.loadFailed'));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Focus στο «Άκυρο», Esc κλείνει — όπως η επιβεβαίωση της αναβάθμισης.
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('[data-autofocus]')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const blocked = !draft || !!draft.published;

  const publish = async () => {
    if (!draft || blocked) return;
    setBusy(true); setPublishError(null);
    try {
      await organizationAPI.publishAgentRelease(draft.commit, notes.trim());
      showToast('success', t('orgDashboard:agent.publish.done', { version: draft.version }));
      onPublished(draft.version);
    } catch (e: any) {
      setPublishError(e?.response?.data?.error || t('orgDashboard:agent.failed'));
      // Ό,τι κι αν έγινε (π.χ. τη δημοσίευσε άλλος), η δοκιμή ξαναδιαβάζεται για να δείχνει την αλήθεια.
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" onClick={() => { if (!busy) onClose(); }} data-testid="org-agent-publish">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${uid}-title`}
        className="bg-surface-1 border border-line rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h2 id={`${uid}-title`} className="text-lg font-bold text-t1 mb-1">{t('orgDashboard:agent.publish.title')}</h2>
        <p className="text-sm text-t3 mb-4">{t('orgDashboard:agent.publish.intro')}</p>

        {!draft && !loadError && (
          <p className="flex items-center gap-2 text-sm text-t3 py-4" data-testid="org-agent-publish-loading">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('orgDashboard:agent.publish.loading')}
          </p>
        )}
        {loadError && <div className="mb-4"><Notice tone="danger" testId="org-agent-publish-load-error">{loadError}</Notice></div>}

        {draft && (
          <>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm mb-4" data-testid="org-agent-publish-draft">
              <dt className="text-t3">{t('orgDashboard:agent.version')}</dt>
              <dd className="text-t1 font-semibold">{draft.version}</dd>
              <dt className="text-t3">{t('orgDashboard:agent.publish.commit')}</dt>
              <dd className="text-t2 min-w-0">
                <code className="text-xs">{draft.commit.slice(0, 8)}</code> · {day(draft.committedAt)}
                <span className="block text-xs text-t3 truncate" title={draft.subject}>{draft.subject}</span>
              </dd>
              <dt className="text-t3">{t('orgDashboard:agent.publish.package')}</dt>
              <dd className="text-t2">{t('orgDashboard:agent.publish.packageSize', { files: draft.fileCount, kb: Math.round(draft.sizeBytes / 1024) })}</dd>
              <dt className="text-t3">{t('orgDashboard:agent.publish.newest')}</dt>
              <dd className="text-t2">{draft.newest ? `${draft.newest.version} · ${day(draft.newest.createdAt)}` : '—'}</dd>
            </dl>

            <div className="space-y-2 mb-4">
              {draft.published && draft.published.sameContent !== false && (
                <Notice testId="org-agent-publish-exists">
                  <span>{rich(t('orgDashboard:agent.publish.existsSame', { version: draft.version, date: day(draft.published.createdAt) }))}</span>
                </Notice>
              )}
              {draft.published && draft.published.sameContent === false && (
                <Notice tone="danger" testId="org-agent-publish-exists-other">
                  <span>{rich(t('orgDashboard:agent.publish.existsOther', { version: draft.version }))}</span>
                </Notice>
              )}
              {!draft.published && draft.depsChanged && draft.newest && (
                <Notice testId="org-agent-publish-deps">
                  <span>{rich(t('orgDashboard:agent.publish.depsChanged', { newest: draft.newest.version }))}</span>
                </Notice>
              )}
              {publishError && <Notice tone="danger" testId="org-agent-publish-error">{publishError}</Notice>}
            </div>

            {!draft.published && (
              <>
                <label htmlFor={`${uid}-notes`} className="block text-sm font-semibold text-t2 mb-1.5">{t('orgDashboard:agent.publish.notes')}</label>
                <textarea
                  id={`${uid}-notes`}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  maxLength={NOTES_MAX}
                  rows={2}
                  placeholder={t('orgDashboard:agent.publish.notesPlaceholder')}
                  className={`${INPUT_CLS} mb-2`}
                  data-testid="org-agent-publish-notes"
                />
                <p className="text-xs text-t3 mb-4">{t('orgDashboard:agent.publish.noTarget')}</p>
              </>
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className={GHOST_BTN} data-autofocus>
            {draft?.published ? t('common:close') : t('orgDashboard:actions.cancel')}
          </button>
          {!draft?.published && (
            <button type="button" onClick={publish} disabled={busy || blocked} className={PRIMARY_BTN} data-testid="org-agent-publish-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Upload className="w-4 h-4" aria-hidden="true" />}
              {draft ? t('orgDashboard:agent.publish.confirm', { version: draft.version }) : t('orgDashboard:agent.publish.title')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentReleasePublish;
